import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight, X } from "@phosphor-icons/react";

/**
 * Full-screen promo shown after login and again before entering /surf.
 * Callers pass `?next=/some-path` to control where the user lands after
 * skipping. When no active deal exists (or the feature is off) we bounce
 * straight to `next` so the offer never blocks the intended navigation.
 */
export default function LoginDeal() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const nextPath = params.get("next") || "/dashboard";
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/login-deal/active");
        if (!data.deal) return nav(nextPath, { replace: true });
        setDeal(data.deal);
      } catch (e) {
        toast.error(formatApiError(e));
        nav(nextPath, { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [nav, nextPath]);

  const skip = () => {
    nav(nextPath, { replace: true });
  };

  if (loading || !deal) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500">Loading…</div>;
  }

  return (
    <div className="min-h-screen flex flex-col bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100">
      <div className="px-6 py-4 flex items-center justify-between border-b border-slate-200 dark:border-slate-800">
        <div className="text-[10px] font-black uppercase tracking-widest text-amber-600 dark:text-amber-400">Special offer</div>
        <button
          data-testid="login-deal-close"
          onClick={skip}
          aria-label="Close"
          className="rounded-full w-9 h-9 inline-flex items-center justify-center bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700"
        >
          <X size={14} />
        </button>
      </div>

      <div className="flex-1 w-full px-6 md:px-10 py-8 md:py-12 space-y-6">
        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter text-slate-900 dark:text-white" style={{ fontFamily: "'Cabinet Grotesk', system-ui, sans-serif" }}>
          {deal.title}
        </h1>
        {(deal.blocks || []).map((b, i) => <DealBlock key={i} block={b} />)}
      </div>

      <div className="px-6 py-6 bg-slate-50 dark:bg-slate-800/50 border-t border-slate-200 dark:border-slate-800 flex flex-col items-center gap-3">
        <div className="text-sm text-slate-500">Not interested right now?</div>
        <button
          data-testid="login-deal-skip"
          onClick={skip}
          className="w-full max-w-3xl inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-black text-white text-xl md:text-2xl font-black uppercase tracking-widest px-8 py-5 md:py-6 shadow-lg hover:shadow-xl transition-all"
        >
          Skip offer <ArrowRight size={22} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function DealBlock({ block }) {
  if (!block) return null;
  const { type, value, image_url, link_url, label } = block;
  const wrapLink = (children) =>
    link_url ? (
      <a href={link_url} target="_blank" rel="noreferrer noopener" className="block">{children}</a>
    ) : (
      children
    );

  if (type === "heading") {
    return <h2 className="text-2xl md:text-4xl font-black tracking-tight text-slate-900 dark:text-white mt-2">{value}</h2>;
  }
  if (type === "text") {
    return <p className="text-lg md:text-xl text-slate-700 dark:text-slate-200 leading-relaxed whitespace-pre-wrap">{value}</p>;
  }
  if (type === "image") {
    return wrapLink(
      <img
        src={image_url || value}
        alt={label || ""}
        className="w-full rounded-2xl border border-slate-200 dark:border-slate-800 hover:opacity-95 transition-opacity"
      />
    );
  }
  if (type === "button") {
    return (
      <div className="w-full flex justify-center mt-2">
        <a
          href={link_url || value}
          target="_blank"
          rel="noreferrer noopener"
          className="w-full max-w-3xl inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 hover:bg-amber-300 text-amber-950 font-black uppercase tracking-widest text-xl md:text-2xl px-8 py-5 md:py-6 shadow-lg hover:shadow-xl transition-all"
        >
          {label || value || "Yes I want this deal before it ends!"} <ArrowRight size={22} weight="bold" />
        </a>
      </div>
    );
  }
  return null;
}
